import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import UniversityIdSummaryTable from '../../components/UniversityIdSummaryTable';
import { batchStatusBadgeClass, formatStatusLabel } from '../../utils/batchStudentStatusHelpers';
import {
  UNIVERSITY_ID_STATUS_OPTIONS,
  universityIdBadgeClass,
  universityIdLabel,
  filterCoursesByUniversityIdStatus,
} from '../../utils/universityIdStatusHelpers';

const VIEW_MODES = [
  { value: 'summary', label: 'Summary Table' },
  { value: 'detailed', label: 'Detailed List' },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#059669' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(5,150,105,0.2)' : 'none',
    '&:hover': { borderColor: '#059669' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#d1fae5' : state.isFocused ? '#ecfdf5' : 'white',
    color: state.isSelected ? '#065f46' : '#1f2937', cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
  multiValueLabel: (base) => ({ ...base, color: '#065f46' }),
};

const BATCH_STATUS_TABS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'not started', label: 'Not Started' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed', label: 'Batch Completed' },
  { value: 'all', label: 'All' },
];

function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function UniversityIdStatusReport() {
  const navigate = useNavigate();

  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const [courseOptions, setCourseOptions] = useState([]);
  const [courseOptionsLoading, setCourseOptionsLoading] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]); // [] = all accessible courses
  const [selectedStatus, setSelectedStatus] = useState('ongoing');
  const [selectedIdStatuses, setSelectedIdStatuses] = useState([]); // [] = both statuses
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('summary');
  const [downloadMode, setDownloadMode] = useState(false);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Management" staff get the same unrestricted report access as Super Admin
  // (see RequireReportAccess in App.js) — without this, they'd otherwise be
  // narrowed down to their own (typically empty) coordinate_courses list.
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  const accessibleParam = useCallback(() => {
    if (isSuperAdmin || isManagement) return 'all';
    return (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
      .map((id) => Number(id))
      .filter((num) => Number.isFinite(num) && num > 0);
  }, [isSuperAdmin, isManagement, accessibleCourseIds]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Derive the course filter's option list across every batch status (not
  // just the currently selected tab) so switching tabs never hides a course
  // from the filter that still has batches under a different status.
  useEffect(() => {
    if (!accessLoaded) return;
    let isMounted = true;
    (async () => {
      setCourseOptionsLoading(true);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/university-id-status', {
          params: { accessibleCourses: accessibleParam(), courseIds: 'all', batchStatus: 'all' },
        });
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.courses) ? resp.data.courses : [];
        const options = list
          .filter((c) => c.courseId)
          .map((c) => {
            const baseName = c.courseShortName || c.courseName || `Course #${c.courseId}`;
            const scheduleLabel = formatScheduleType(c.scheduleType);
            return { value: c.courseId, label: scheduleLabel ? `${baseName} (${scheduleLabel})` : baseName };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        setCourseOptions(options);
      } catch (e) {
        console.error('Failed to load course options', e);
      } finally {
        if (isMounted) setCourseOptionsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [accessLoaded, accessibleParam]);

  const loadReport = useCallback(async () => {
    if (!accessLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/university-id-status', {
        params: {
          accessibleCourses: accessibleParam(),
          courseIds: selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all',
          batchStatus: selectedStatus,
        },
      });
      const data = resp?.data || {};
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load university ID status report');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleParam, selectedCourses, selectedStatus]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Search only — used as-is by the Summary Table view, which always breaks
  // down both University ID statuses regardless of the status filter.
  const searchFilteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return courses;
    return courses
      .map((course) => ({
        ...course,
        batches: course.batches
          .map((batch) => ({
            ...batch,
            students: batch.students.filter((s) => {
              const combined = [s.studentName, s.studentId, s.registrationNumber].filter(Boolean).join(' ').toLowerCase();
              return combined.includes(term)
                || (batch.batchInternalName || '').toLowerCase().includes(term)
                || (batch.batchName || '').toLowerCase().includes(term)
                || (course.courseName || '').toLowerCase().includes(term);
            }),
          }))
          .filter((batch) => batch.students.length > 0
            || (batch.batchInternalName || '').toLowerCase().includes(term)
            || (batch.batchName || '').toLowerCase().includes(term)),
      }))
      .filter((course) => course.batches.length > 0);
  }, [courses, search]);

  // Search + University ID Status filter — used by the Detailed List view.
  const filteredCourses = useMemo(
    () => filterCoursesByUniversityIdStatus(searchFilteredCourses, selectedIdStatuses.map((o) => o.value)),
    [searchFilteredCourses, selectedIdStatuses]
  );

  const activeCourses = viewMode === 'summary' ? searchFilteredCourses : filteredCourses;

  const visibleCounts = useMemo(() => {
    let batches = 0;
    let students = 0;
    activeCourses.forEach((course) => {
      batches += course.batches.length;
      course.batches.forEach((batch) => {
        students += batch.students.length;
      });
    });
    return { batches, students };
  }, [activeCourses]);

  const handlePrint = () => {
    const params = new URLSearchParams();
    params.set('courseIds', selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all');
    params.set('batchStatus', selectedStatus);
    params.set('idStatus', selectedIdStatuses.length > 0 ? selectedIdStatuses.map((o) => o.value).join(',') : 'all');
    params.set('view', viewMode);
    window.open(`/admin/report/university-id-status/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout title="University ID Status Report">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/admin/students/university-registration-upload')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Updated Sheet
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
              </svg>
              Print PDF
            </button>
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            University ID Status Report
          </h2>
          <p className="mt-1 text-sm text-emerald-900">
            Batches grouped by course, showing which students have been assigned a University ID. Pick a batch
            stage below, and leave courses unselected to include every accessible course.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {BATCH_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setSelectedStatus(tab.value)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  selectedStatus === tab.value
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-lg bg-white p-1 ring-1 ring-inset ring-gray-300 w-fit">
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setViewMode(mode.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    viewMode === mode.value
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {viewMode === 'summary' && (
              <button
                type="button"
                onClick={() => setDownloadMode((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  downloadMode
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {downloadMode ? 'Hide Download Buttons' : 'Show Download Buttons'}
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700">Courses</label>
              <Select
                isMulti
                isLoading={courseOptionsLoading}
                options={courseOptions}
                value={selectedCourses}
                onChange={(opts) => setSelectedCourses(opts || [])}
                placeholder="All accessible courses (default)…"
                styles={selectStyles}
                closeMenuOnSelect={false}
              />
            </div>
            {viewMode === 'detailed' && (
              <div className="min-w-[240px] flex-1">
                <label className="mb-1 block text-xs font-medium text-gray-700">University ID Status</label>
                <Select
                  isMulti
                  options={UNIVERSITY_ID_STATUS_OPTIONS}
                  value={selectedIdStatuses}
                  onChange={(opts) => setSelectedIdStatuses(opts || [])}
                  placeholder="Both statuses (default)…"
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                />
              </div>
            )}
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Student / Batch / Course"
                className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            {visibleCounts.batches} batch{visibleCounts.batches === 1 ? '' : 'es'} &middot; {visibleCounts.students} student{visibleCounts.students === 1 ? '' : 's'}
          </h3>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading university ID status…
          </div>
        ) : activeCourses.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            No batches match the current filters.
          </div>
        ) : viewMode === 'summary' ? (
          <div className="space-y-10">
            {activeCourses.map((course) => (
              <div key={course.courseId ?? course.courseName} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <UniversityIdSummaryTable course={course} downloadMode={downloadMode} />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-10">
            {filteredCourses.map((course) => (
              <div key={course.courseId ?? course.courseName}>
                <h3 className="mb-3 text-lg font-bold text-gray-900">
                  {course.courseShortName || course.courseName || 'Unknown Course'}
                  {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                </h3>

                <div className="space-y-5">
                  {course.batches.map((batch) => (
                    <div key={batch.batchId} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-gray-800">
                              {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
                            </p>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${batchStatusBadgeClass(batch.status)}`}>
                              {formatStatusLabel(batch.status)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                          {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px] text-left">
                          <thead>
                            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              <th className="px-5 py-2.5">Student</th>
                              <th className="px-5 py-2.5">Student ID</th>
                              <th className="px-5 py-2.5">Registration No.</th>
                              <th className="px-5 py-2.5">University ID</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {batch.students.map((s) => (
                              <tr key={`${s.studentId}-${s.registrationNumber}-${batch.batchId}`} className="hover:bg-gray-50">
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={getImageURL(s.imagePath)}
                                      alt={s.studentName}
                                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
                                    />
                                    <span className="max-w-[180px] truncate text-sm font-medium text-gray-800">{s.studentName || '—'}</span>
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-xs text-gray-600">{s.studentId || '—'}</td>
                                <td className="px-5 py-3 text-xs text-gray-600">{s.registrationNumber || '—'}</td>
                                <td className="px-5 py-3">
                                  <span className={`inline-block rounded-full px-2 py-1 text-xs font-bold ${universityIdBadgeClass(s.universityID)}`}>
                                    {universityIdLabel(s.universityID)}
                                  </span>
                                  <span className="ml-2 text-[10px] text-gray-400">Updated: {formatDate(s.universityRegisterDate)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
